import { CalculationParameterConfigurationType } from "#batteries-included-components/Tabs/EstimationMethod/ConfigurationTab/ConfigurationTab";
import { DateRange } from "#src/batteries-included-components/Forms/DateRange";
import { useMeasurementTypes } from "#src/contexts/MeasurementTypeContext";
import { isNumeric } from "#src/utils/unitConverter";
import { useMutation } from "@tanstack/react-query";
import {
  Dialog,
  Form,
  FormButton,
  useForm,
  useToast,
} from "@validereinc/common-components";
import type { AssetTypeType } from "@validereinc/domain";
import { EstimationMethodAdapter } from "@validereinc/domain";
import { yearMonthFormatter } from "@validereinc/utilities";
import { startOfMonth } from "date-fns";
import { merge } from "es-toolkit/compat";
import flatten from "flat";
import React, { useEffect } from "react";
import { getCalculationParameterInput } from "../helpers";

export const DefaultValueDialog = ({
  methodId,
  entityType,
  itemToEdit,
  defaultValues,
  onSubmit,
  onClose,
}: {
  methodId?: string;
  entityType?: AssetTypeType;
  itemToEdit?: CalculationParameterConfigurationType;
  defaultValues: Record<string, any>;
  onSubmit?: () => void;
  onClose?: () => void;
}) => {
  const { toast } = useToast();
  const { getUnitName } = useMeasurementTypes();

  const form = useForm({
    defaultValues: itemToEdit
      ? { [itemToEdit?.id]: itemToEdit?.default_value?.value }
      : {},
  });

  useEffect(() => {
    form.reset();
  }, [itemToEdit]);

  const updateEstimationMethod = useMutation({
    mutationFn: async (
      inputs: Record<string, string> & {
        dateRange: {
          from?: Date;
          to?: Date;
        };
      }
    ) => {
      try {
        if (!itemToEdit || !methodId || !entityType) {
          throw new Error("Estimation method identifiers unavailable");
        }

        const value = flatten(inputs, { safe: true })[itemToEdit.id];
        const isValueSet = value && value !== "";

        await EstimationMethodAdapter.updateOne({
          id: methodId,
          // This is a workaround so we don't delete peer nested default_values
          data: {
            default_values: merge(defaultValues, {
              [itemToEdit.id]: isValueSet
                ? {
                    // TODO: Sometimes we get invalid measurement types from analytics
                    // measurement_quantity: itemToEdit.measurement_quantity,
                    // measurement_type: itemToEdit.measurement_type,
                    unit: itemToEdit.measurement_unit,
                    value: isNumeric(value) ? +value : value,
                  }
                : null,
            }),
            start_date:
              inputs.dateRange?.from && inputs.hasDateRangeStart
                ? yearMonthFormatter(startOfMonth(inputs.dateRange.from))
                : "min",
            end_date:
              inputs.dateRange?.to && inputs.hasDateRangeEnd
                ? yearMonthFormatter(startOfMonth(inputs.dateRange?.to))
                : "max",
          },
          meta: { entityType },
        });
        onClose?.();
        onSubmit?.();
        toast.push({
          intent: "success",
          description: `Updated default value for ${itemToEdit?.display_name}`,
        });
      } catch (err) {
        toast.push({
          intent: "error",
          description: "Unable to update default value",
        });
      }
    },
  });

  return (
    <Form
      {...form}
      onSubmit={form.handleSubmit(updateEstimationMethod.mutate as any) as any}
    >
      <Dialog
        isOpen={!!itemToEdit}
        onClose={onClose ?? (() => {})}
        title="Define Default Value"
        actionRow={[
          <FormButton
            key="save-default-values"
            variant="primary"
            onClick={form.handleSubmit(updateEstimationMethod.mutate as any)}
          >
            Save
          </FormButton>,
        ]}
      >
        {itemToEdit
          ? getCalculationParameterInput({
              ...itemToEdit,
              required: false,
              measurement_unit: getUnitName(itemToEdit.measurement_unit),
            })
          : null}
        <DateRange />
      </Dialog>
    </Form>
  );
};
